<template>
  <div class="opportunity-tasks">
    <OpportunityHeader />
    <BaseLoadingBar v-if="taskStatusesPending" />

    <template v-else>
      <BaseLoadingBar v-if="tasksPending" />
      <div :class="`opportunity-tasks__main ${!selectedTask ? 'opportunity-tasks__main--no-dialog' : ''}`">
        <div :class="`opportunity-tasks__cards ${!selectedTask ? 'opportunity-tasks__cards-fullscreen' : ''}`">
          <TasksOverview :tasks="tasks" :task-statuses="taskStatuses" />
          <TasksCard
            :tasks="tasks"
            @task-click="onTaskSelect"
            @task-assign="onTaskAssign"
            @task-create="onCreateTask"
            @tasks-import="onImportTasks"
          />
        </div>
        <transition name="task-dialog-slide" @after-enter="onTaskDialogTransitionEnd">
          <div v-if="selectedTask" key="task-dialog" class="opportunity-tasks__dialog">
            <TaskDialog
              ref="taskDialog"
              :task="selectedTask"
              @close="onTaskDialogClose"
              @submit="onTaskEdit"
              @delete-citation="onTaskCitationDelete"
            />
          </div>
        </transition>
      </div>
    </template>
  </div>
</template>

<script>
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import OpportunityHeader from '@/components/page-opportunities/OpportunityHeader';
import TaskDialog from '@/components/page-opportunity-tasks/TaskDialog.vue';
import TasksCard from '@/components/page-opportunity-tasks/TasksCard.vue';
import TasksOverview from '@/components/page-opportunity-tasks/TasksOverview.vue';
import { paths } from '@/util/paths';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PageOpportunityTasks',
  components: {
    BaseLoadingBar,
    OpportunityHeader,
    TaskDialog,
    TasksCard,
    TasksOverview,
  },
  props: {
    opportunityId: String,
  },
  data() {
    return {
      selectedTask: null,
    };
  },
  computed: {
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('tasks', ['tasks']),
    ...mapGetters('tasks', { tasksPending: 'pending' }),
    ...mapGetters('taskStatuses', ['taskStatuses']),
    ...mapGetters('taskStatuses', { taskStatusesPending: 'pending' }),
  },
  methods: {
    ...mapActions('opportunity', ['getOpportunity']),
    ...mapActions('tasks', [
      'getOpportunityTasks',
      'updateOpportunityTaskAssignees',
      'generateOpportunityTasks',
      'RESET',
    ]),
    ...mapActions('taskStatuses', ['getTaskStatuses']),
    ...mapActions('taskTemplates', ['getTaskTemplates']),
    ...mapActions('annotations', ['getOpportunityDocumentsAnnotations']),
    ...mapActions('extractionJob', ['watchExtractionJob']),
    handleError(error) {
      if (!error.response) this.$router.replace(paths.notFound);

      if (error.response.status === 404 || error.response.status === 403) {
        this.$router.replace(paths.notFound);
      } else {
        console.error(error);
        this.$toast.error(error);
        throw error;
      }
    },
    onTaskSelect(task) {
      this.selectedTask = task;
    },
    onCreateTask() {
      this.selectedTask = {
        id: null,
        opportunityId: this.opportunityId,
        title: '',
        description: '',
        taskStatus: null,
        assignations: [],
        citations: [],
      };
    },
    onTaskDialogClose() {
      this.selectedTask = null;
    },
    onTaskEdit(updatedTask) {
      this.selectedTask = updatedTask;
    },
    onTaskCitationDelete(citation) {
      this.selectedTask.citations = this.selectedTask.citations.filter((c) => c.id !== citation.id);
    },
    onTaskAssign({ task, userIds }) {
      this.updateOpportunityTaskAssignees({
        opportunityId: task.opportunityId,
        taskId: task.id,
        assigneeIds: userIds,
      }).then((updatedTask) => {
        if (updatedTask.id === this.selectedTask.id) {
          this.selectedTask = updatedTask;
        }
      });
    },
    onTaskDialogTransitionEnd() {
      const isCreateTaskMode = this.selectedTask && !this.selectedTask.id;
      if (this.$refs.taskDialog && isCreateTaskMode) {
        this.$refs.taskDialog.focusOnTitleInput();
      }
    },
    onImportTasks() {
      this.generateOpportunityTasks({ opportunityId: this.opportunityId }).then(() => {
        const totalTasksFromTemplate = this.tasks.filter((t) => !!t.taskTemplateId).length;
        this.$toast.success(this.$t('opportunity-tasks.import-tasks-success', { total: totalTasksFromTemplate }));
      });
    },
  },
  async created() {
    try {
      const opportunityId = this.opportunityId;

      if (!this.opportunity || this.opportunity.id !== opportunityId) {
        const opportunity = await this.getOpportunity({ id: opportunityId });
        if (!opportunity) return;
      }

      await this.getOpportunityTasks({ opportunityId });
      await this.getTaskStatuses();
      await this.getTaskTemplates();
      this.getOpportunityDocumentsAnnotations({
        opportunityId,
      });
      this.watchExtractionJob({ opportunityId: this.opportunityId });
    } catch (error) {
      this.handleError(error);
    }
  },
  destroyed() {
    this.RESET();
  },
};
</script>

<style lang="scss">
.opportunity-tasks {
  display: flex;
  flex-direction: column;
  height: calc(var(--viewport-height) - 56px);
  width: 100%;

  &__main {
    display: flex;
    flex: 1;
    overflow-x: hidden;
    position: relative;

    // make the scrollbar in the main container
    &--no-dialog {
      overflow-y: auto;
    }
  }

  &__cards {
    width: 616px;
    max-width: 100%;
    padding: 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex-shrink: 0;
    transition: width 0.4s cubic-bezier(0.25, 0.8, 0.25, 1), margin 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);

    &-fullscreen {
      width: 100%;
      max-width: 1200px;
      // equivalent to 'auto' but calculated manually to have explicit px values to interpolate from in the transition
      margin-left: max(0px, calc((100% - 1200px) / 2));
      margin-right: max(0px, calc((100% - 1200px) / 2));
      overflow-y: visible; // disable its scrollbar because it is handled by the main container
    }
  }

  &__dialog {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    display: flex;

    .task-dialog {
      flex: 1;
    }
  }

  @media screen and (max-width: 900px) {
    &__main {
      flex-direction: column;
    }

    &__dialog {
      min-height: 50vh;
    }
  }

  @media screen and (max-width: 620px) {
    &__cards {
      padding: 20px 0;
    }
    &__dialog {
      padding: 0;
    }
  }
}

.task-dialog-slide-enter-active {
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.task-dialog-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: calc(100% - 616px);
}

.task-dialog-slide-enter,
.task-dialog-slide-leave-to {
  transform: translateX(100%);
}

.task-dialog-slide-enter-to,
.task-dialog-slide-leave {
  transform: translateX(0);
}
</style>
